import React, { memo, useEffect, useState } from 'react';

import {
  IconArrowRight03,
  IconArrowRight03D,
} from '@wconcept/wconcept-ui-renewal/components/atoms/svg';
import Button from '@wconcept/wconcept-ui-renewal/components/atoms/Button';

import * as S from './style';

interface IProps {
  /** 현재 페이지 */
  activePage?: number;
  /** 한 페이지당 보여줄 리스트 아이템의 개수 */
  itemsCountPerPage?: number | undefined;
  /** Paginator 내에서 보여줄 페이지의 범위 */
  totalItemsCount?: number;
  // totlaPages
  totalPages?: number;
  /** 총 아이템의 개수 */
  pageRangeDisplayed?: number;
  /** 자동 페이징 이동 */
  autoMove?: boolean;
  /** onPageChange */
  onPageChange: (selectedPage: number) => void;
  /** true면 마지막 페이지(맨 끝) 이동 버튼 미노출 */
  hideLastPageButton?: boolean;
}

const Paging = ({
  activePage = 1,
  itemsCountPerPage = 5,
  pageRangeDisplayed = 10,
  totalItemsCount = 0,
  totalPages = 1,
  autoMove = false,
  hideLastPageButton = false,
  onPageChange,
}: IProps) => {
  const [currentTotalPages, setCurrentTotalPages] = useState(totalPages);

  useEffect(() => {
    if (totalPages > 0) {
      setCurrentTotalPages(totalPages);
      return;
    }
    if (totalItemsCount) {
      const pages = Math.ceil(totalItemsCount / itemsCountPerPage);
      if (pages) {
        setCurrentTotalPages(pages);
      }
    }
  }, [totalItemsCount, totalPages]);

  const handlePageChange = (pageNumber: number) => {
    onPageChange(pageNumber);
  };

  const handleFirstPage = () => {
    onPageChange(1);
  };

  const handleLastPage = () => {
    onPageChange(currentTotalPages);
  };

  const handlePrevPage = () => {
    const prevPage = Math.floor((activePage - 1) / 10) * 10;
    onPageChange(prevPage === 0 ? 1 : prevPage);
  };

  const handleNextPage = () => {
    const nextPage = Math.ceil(activePage / 10) * 10 + 1;
    onPageChange(nextPage > currentTotalPages ? currentTotalPages : nextPage);
  };

  const renderPages = () => {
    const pageNumbers = [];
    let startPageNumber;
    let endPageNumber;

    if (autoMove) {
      // 자동 이동
      const halfRangeDisplayed = Math.floor(pageRangeDisplayed / 2);
      startPageNumber = Math.max(1, activePage - halfRangeDisplayed);
      endPageNumber = Math.min(
        currentTotalPages,
        activePage + halfRangeDisplayed
      );

      if (totalPages <= pageRangeDisplayed) {
        startPageNumber = 1;
        endPageNumber = currentTotalPages;
      } else if (activePage <= halfRangeDisplayed) {
        endPageNumber = pageRangeDisplayed;
      } else if (activePage >= currentTotalPages - halfRangeDisplayed) {
        startPageNumber = currentTotalPages - pageRangeDisplayed + 1;
      }
    } else {
      // 수동 이동
      startPageNumber = Math.max(
        1,
        Math.floor((activePage - 1) / pageRangeDisplayed) * pageRangeDisplayed +
          1
      );
      endPageNumber = Math.min(
        currentTotalPages,
        startPageNumber + (pageRangeDisplayed - 1)
      );

      if (currentTotalPages <= pageRangeDisplayed) {
        startPageNumber = 1;
        endPageNumber = currentTotalPages;
      }
    }

    for (let i = startPageNumber; i <= endPageNumber; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers.map(page => (
      <li key={page} className={`${activePage === page ? 'active' : ''}`}>
        <Button onClick={() => handlePageChange(page)}>{page}</Button>
      </li>
    ));
  };

  return (
    <S.Pagination className="wrap-pagination">
      <ul className="pagination">
        <li
          className={`btn-first ${
            activePage <= pageRangeDisplayed ? 'disabled' : ''
          }`}
        >
          <Button onClick={handleFirstPage}>
            <IconArrowRight03D />
          </Button>
        </li>
        <li
          className={`btn-prev ${
            activePage <= pageRangeDisplayed ? 'disabled' : ''
          }`}
        >
          <Button onClick={handlePrevPage}>
            <IconArrowRight03 />
          </Button>
        </li>
        {renderPages()}
        <li
          className={`btn-next ${
            currentTotalPages < pageRangeDisplayed ||
            currentTotalPages === activePage
              ? 'disabled'
              : ''
          }`}
        >
          <Button onClick={handleNextPage}>
            <IconArrowRight03 />
          </Button>
        </li>
        {!hideLastPageButton && (
          <li
            className={`btn-last ${
              currentTotalPages < pageRangeDisplayed ||
              currentTotalPages === activePage
                ? 'disabled'
                : ''
            }`}
          >
            <Button onClick={handleLastPage}>
              <IconArrowRight03D />
            </Button>
          </li>
        )}
      </ul>
    </S.Pagination>
  );
};

export default memo(Paging);
